import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSupabase, useTenant } from "@how/app-kit";
import type { SemanticRole } from "@how/design-system";

function useTenantId(): string | null {
  const tenant = useTenant();
  return tenant.status === "resolved" ? tenant.tenant.tenantId : null;
}

export interface TagRow {
  readonly id: string;
  readonly name: string;
  readonly color: SemanticRole;
  readonly description: string | null;
  readonly peopleCount: number;
}

/**
 * The church's tags with how many people carry each. A church has tens of tags, not thousands, so the list is read
 * whole and sorted by name rather than paged.
 */
export function useTags() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["tags", tenantId],
    enabled: tenantId !== null,
    queryFn: async (): Promise<TagRow[]> => {
      const { data, error } = await supabase
        .from("tags")
        .select("id, name, color_role, description, person_tags(count)")
        // A reader of two churches would otherwise be offered the other church's tags, which the tagging function
        // then rejects as not found.
        .eq("tenant_id", tenantId ?? "")
        .order("name");
      if (error) throw error;
      return data.map((row) => ({
        id: row.id,
        name: row.name,
        // The column's check constraint admits exactly the design-system role names.
        color: row.color_role as SemanticRole,
        description: row.description,
        peopleCount: row.person_tags[0]?.count ?? 0,
      }));
    },
  });
}

function useTagInvalidation() {
  const client = useQueryClient();
  const tenantId = useTenantId();
  return async () => {
    await client.invalidateQueries({ queryKey: ["tags", tenantId] });
    // A tag's name and colour show on every record that carries it, and the directory filters by it.
    await client.invalidateQueries({ queryKey: ["person", tenantId] });
    await client.invalidateQueries({ queryKey: ["people", tenantId] });
  };
}

export interface TagFields {
  readonly name: string;
  readonly color: SemanticRole;
  readonly description: string;
}

export function useSaveTag() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const invalidate = useTagInvalidation();
  return useMutation({
    mutationFn: async (input: { readonly tagId: string | null; readonly fields: TagFields }) => {
      const description = input.fields.description.trim();
      const shared = {
        p_name: input.fields.name.trim(),
        p_color_role: input.fields.color,
        ...(description.length === 0 ? {} : { p_description: description }),
      };
      const { error } =
        input.tagId === null
          ? await supabase.rpc("create_tag", { p_tenant_id: tenantId ?? "", ...shared })
          : await supabase.rpc("update_tag", { p_tag_id: input.tagId, ...shared });
      if (error) throw error;
    },
    onSuccess: invalidate,
  });
}

export function useDeleteTag() {
  const supabase = useSupabase();
  const invalidate = useTagInvalidation();
  return useMutation({
    mutationFn: async (tagId: string) => {
      const { error } = await supabase.rpc("delete_tag", { p_tag_id: tagId });
      if (error) throw error;
    },
    onSuccess: invalidate,
  });
}

export function useTagPerson() {
  const supabase = useSupabase();
  const invalidate = useTagInvalidation();
  return useMutation({
    mutationFn: async (input: { readonly personId: string; readonly tagId: string }) => {
      const { error } = await supabase.rpc("add_person_tag", {
        p_person_id: input.personId,
        p_tag_id: input.tagId,
      });
      if (error) throw error;
    },
    onSuccess: invalidate,
  });
}

export function useUntagPerson() {
  const supabase = useSupabase();
  const invalidate = useTagInvalidation();
  return useMutation({
    mutationFn: async (personTagId: string) => {
      const { error } = await supabase.rpc("remove_person_tag", { p_person_tag_id: personTagId });
      if (error) throw error;
    },
    onSuccess: invalidate,
  });
}
